import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import api, { currency, formatApiError } from "@/lib/api";

const STATUS_COLORS = {
  Estimate: "text-zinc-300 bg-zinc-700/40",
  Approved: "text-green-400 bg-green-500/15",
  "In Progress": "text-yellow-400 bg-yellow-500/15",
  Completed: "text-blue-400 bg-blue-500/15",
  Declined: "text-red-400 bg-red-500/15",
};
const FLOW = ["Estimate", "Approved", "In Progress", "Completed"];
const inputCls = "bg-zinc-900 border border-zinc-800 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-shadow duration-150";

function Builder({ customers, onClose, onSaved }) {
  const [customerId, setCustomerId] = useState(customers[0]?.id || "");
  const [category, setCategory] = useState("commercial");
  const [vehicle, setVehicle] = useState({ type: "commercial", year: "", make: "", model: "", plate: "" });
  const [items, setItems] = useState([{ id: crypto.randomUUID(), type: "part", description: "", qty: 1, unit_price: 0 }]);
  const [saving, setSaving] = useState(false);

  const addItem = (type) => setItems([...items, { id: crypto.randomUUID(), type, description: "", qty: 1, unit_price: 0 }]);
  const upd = (id, k, v) => setItems(items.map((i) => (i.id === id ? { ...i, [k]: v } : i)));
  const del = (id) => setItems(items.filter((i) => i.id !== id));
  const subtotal = items.reduce((s, i) => s + Number(i.qty) * Number(i.unit_price), 0);

  const save = async () => {
    if (!customerId) return toast.error("Select a customer");
    setSaving(true);
    try {
      await api.post("/builds", {
        customer_id: customerId, category, vehicle: { ...vehicle, type: category },
        line_items: items.map((i) => ({ ...i, qty: Number(i.qty), unit_price: Number(i.unit_price) })),
      });
      toast.success("Estimate created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto p-6" data-testid="build-modal">
      <div className="bg-zinc-950 border border-zinc-800 w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="font-display text-xl font-bold uppercase">New Estimate</h2>
          <button onClick={onClose} data-testid="build-modal-close"><X className="text-zinc-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase text-zinc-400 block mb-1">Customer</label>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={`${inputCls} w-full`} data-testid="build-customer">
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase text-zinc-400 block mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} w-full`} data-testid="build-category">
                <option value="commercial">Commercial / Heavy-Duty</option><option value="passenger">Passenger Vehicle</option><option value="custom">Custom Build</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {["year", "make", "model", "plate"].map((f) => (
              <input key={f} placeholder={f} value={vehicle[f]} onChange={(e) => setVehicle({ ...vehicle, [f]: e.target.value })} className={inputCls} data-testid={`build-vehicle-${f}`} />
            ))}
          </div>

          <div className="border border-zinc-800">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs uppercase text-zinc-500 border-b border-zinc-800">
              <span className="col-span-2">Type</span><span className="col-span-5">Description</span><span className="col-span-1">Qty</span><span className="col-span-2">Unit $</span><span className="col-span-1 text-right">Total</span><span />
            </div>
            {items.map((i) => (
              <div key={i.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-zinc-900" data-testid="build-lineitem-row">
                <select value={i.type} onChange={(e) => upd(i.id, "type", e.target.value)} className={`${inputCls} col-span-2`}>
                  <option value="part">Part</option><option value="labor">Labor</option>
                </select>
                <input value={i.description} onChange={(e) => upd(i.id, "description", e.target.value)} className={`${inputCls} col-span-5`} placeholder="Description" />
                <input type="number" value={i.qty} onChange={(e) => upd(i.id, "qty", e.target.value)} className={`${inputCls} col-span-1`} />
                <input type="number" value={i.unit_price} onChange={(e) => upd(i.id, "unit_price", e.target.value)} className={`${inputCls} col-span-2`} />
                <span className="col-span-1 text-right text-sm">{currency(i.qty * i.unit_price)}</span>
                <button onClick={() => del(i.id)} className="col-span-1 text-zinc-500 hover:text-red-500 flex justify-end"><Trash2 size={16} /></button>
              </div>
            ))}
            <div className="flex gap-3 p-3">
              <button onClick={() => addItem("part")} className="text-xs uppercase font-bold text-yellow-500 flex items-center gap-1" data-testid="build-add-part"><Plus size={14} /> Part</button>
              <button onClick={() => addItem("labor")} className="text-xs uppercase font-bold text-yellow-500 flex items-center gap-1" data-testid="build-add-labor"><Plus size={14} /> Labor</button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-zinc-400 uppercase text-sm">Subtotal</span>
            <span className="font-display text-2xl font-black text-yellow-500">{currency(subtotal)}</span>
          </div>
          <button onClick={save} disabled={saving} data-testid="build-save"
            className="w-full bg-yellow-500 text-black font-bold uppercase py-3 hover:bg-yellow-300 transition-colors duration-150 disabled:opacity-60">
            {saving ? "Saving…" : "Create Estimate"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Builds() {
  const [builds, setBuilds] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);

  const load = () => {
    api.get("/builds").then(({ data }) => setBuilds(data));
    api.get("/customers").then(({ data }) => setCustomers(data));
  };
  useEffect(load, []);

  const advance = async (b) => {
    const idx = FLOW.indexOf(b.status);
    if (idx >= FLOW.length - 1) return;
    const next = FLOW[idx + 1];
    try {
      await api.patch(`/builds/${b.id}/status`, { status: next });
      toast.success(`Moved ${b.build_number} → ${next}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const makeInvoice = async (b) => {
    try {
      await api.post(`/invoices/from-build/${b.id}`);
      toast.success(`Invoice created for ${b.build_number}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendInvite = async (b) => {
    try {
      await api.post(`/builds/${b.id}/send-invite`);
      toast.success(`Portal invite emailed to ${b.customer_email}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tighter">Builds & Estimates</h1>
          <p className="text-zinc-500 text-sm">Parts + labor estimate builder & status pipeline</p>
        </div>
        <button onClick={() => setShowBuilder(true)} data-testid="new-build-btn"
          className="bg-yellow-500 text-black font-bold uppercase px-5 py-3 flex items-center gap-2 hover:bg-yellow-300 transition-colors duration-150">
          <Plus size={18} /> New Estimate
        </button>
      </div>

      <div className="border border-zinc-800 bg-zinc-950">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-zinc-500 uppercase text-xs">
              <th className="px-4 py-3">Build</th><th>Customer</th><th>Vehicle</th><th className="text-right">Subtotal</th><th className="text-right">Approved</th><th>Status</th><th className="text-right px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {builds.map((b) => (
              <tr key={b.id} className="border-t border-zinc-800 even:bg-zinc-900/40" data-testid={`build-row-${b.build_number}`}>
                <td className="px-4 py-3 font-semibold">{b.build_number}</td>
                <td>{b.customer_name}</td>
                <td className="text-zinc-400">{b.vehicle.year} {b.vehicle.make} {b.vehicle.model}</td>
                <td className="text-right">{currency(b.subtotal)}</td>
                <td className="text-right text-green-400">{currency(b.approved_total)}</td>
                <td><span className={`text-xs font-bold uppercase px-2 py-1 ${STATUS_COLORS[b.status]}`}>{b.status}</span></td>
                <td className="text-right px-4 py-3 space-x-2 whitespace-nowrap">
                  {(b.status === "Estimate" || b.status === "Approved") && (
                    <button onClick={() => sendInvite(b)} data-testid={`invite-${b.build_number}`}
                      className="text-xs font-bold uppercase border border-zinc-700 px-3 py-1.5 hover:border-yellow-500 hover:text-yellow-500 transition-colors duration-150">
                      Email Invite
                    </button>
                  )}
                  {b.status !== "Completed" && b.status !== "Declined" && (
                    <button onClick={() => advance(b)} data-testid={`advance-${b.build_number}`}
                      className="text-xs font-bold uppercase border border-zinc-700 px-3 py-1.5 hover:border-yellow-500 hover:text-yellow-500 transition-colors duration-150">
                      Advance
                    </button>
                  )}
                  {(b.status === "Completed" || b.status === "In Progress") && (
                    <button onClick={() => makeInvoice(b)} data-testid={`invoice-${b.build_number}`}
                      className="text-xs font-bold uppercase bg-yellow-500 text-black px-3 py-1.5 hover:bg-yellow-300 transition-colors duration-150">
                      Invoice
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showBuilder && <Builder customers={customers} onClose={() => setShowBuilder(false)} onSaved={() => { setShowBuilder(false); load(); }} />}
    </div>
  );
}
